import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Modal, ModalHeader, ModalBody } from './index';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {
  CosmosButton,
  CosmosSpinner,
  CosmosText,
  CosmosTitle
} from '../../cosmos/web-scoped/react';
import { AppContext } from '../../App';

import './select.scss';
import { getTrans } from '../../Utils/translate';

const ModalSelect = ({
  className,
  children,
  headerText = '',
  options,
  filterVals,
  value,
  selectFirst,
  onChange,
  style = {},
  disabledOptions,
  name = 'needed',
  type = 'default',
  buttonKind = 'secondary',
  buttonShape = 'rectangle',
  buttonWidth = 'full',
  buttonLabel = '',
  fullWidth = false,
  description = false,
  comparison = false
}) => {
  const context = React.useContext(AppContext);
  const animationConfig = context.config.animation;
  const containerAnimation = {
    hidden: {
      opacity: 1,
      scale: 0,
      transition: {
        duration: animationConfig.duration,
        ease: animationConfig.ease
      }
    },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        when: 'beforeChildren',
        staggerChildren: 0.1,
        duration: animationConfig.duration,
        ease: animationConfig.ease
      }
    }
  };
  const itemAnimation = {
    hidden: {
      y: 20,
      opacity: 0,
      transition: {
        duration: animationConfig.duration,
        ease: animationConfig.ease
      }
    },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: animationConfig.duration,
        ease: animationConfig.ease
      }
    }
  };
  const [visible, setVisible] = useState(false);
  const [headerBounds, setHeaderBounds] = useState({ width: 0, height: 0 });
  const [filter] = useState('');
  const [filterOptions, setFilterOptions] = useState(undefined);

  const currentValue = value ? value : selectFirst ? options[0] : '';
  const lang = context.lang;

  const headerRef = useRef(null);

  useEffect(() => {
    if (visible) {
      // init UI
      setHeaderBounds({
        width: headerRef && headerRef.current ? headerRef.current.clientWidth : 0,
        height: headerRef && headerRef.current ? headerRef.current.clientHeight : 0
      });
    }
  }, [visible]);

  if (!options || options.length <= 1) return <></>;

  const getLabel = (label) => {
    if (getTrans(label) !== undefined) {
      return getTrans(label);
    } else return label;
  };

  const getValue = (value) => {
    switch (value) {
      case 'All':
        return getTrans(lang.All);
      case 'top 5':
        return getTrans(lang.top5);
      case 'top 10':
        return getTrans(lang.top10);
      case 'top 15':
        return getTrans(lang.top15);
      case 'top 20':
        return getTrans(lang.top20);
      case 'Current on Track':
        return getTrans(lang.currentOnTrack);
      case 'World Cup Leader':
        return getTrans(lang.worldCupLeader);
      default:
        return value;
    }
  };

  return (
    <>
      <div
        className={`modalSelect ${type} ${visible ? ' open' : ''}${className ? ` ${className}` : ''
          }`}
        style={{
          width: fullWidth ? '100%' : undefined,
          minHeight: description ? "59px" : comparison ? "36px" : "0px"
        }}>
        <div className={`current`}>
          {children &&
            <div onClick={() => setVisible(true)}>
              {children}
            </div>
          }
          {!children &&
            <CosmosButton
              onClick={() => setVisible(true)}
              class="modal-select__button"
              kind={buttonKind}
              shape={buttonShape}
              width={buttonWidth}>
              {description && currentValue.descriptionLabel && (
                <CosmosText className="descriptionValue" kind="subtle" size="x-small">
                  {getValue(currentValue.descriptionLabel)}
                </CosmosText>
              )}
              {getLabel(buttonLabel || currentValue.selectedLabel || currentValue.label)}
            </CosmosButton>
          }
        </div>

        <Modal
          className={`modal-seamless`}
          show={visible}
          setShow={setVisible}>
          <ModalHeader ref={headerRef}>
            <div className="modal__header-title">
              <CosmosTitle>{headerText}</CosmosTitle>
            </div>
            <div className="modal__header-sub">
              {filterVals && (
                <div className={`select-filterVals`}>
                  <ul className={`select-filterList`}>
                    <li
                      className={`filterValue${!filterOptions ? ' active' : ''}`}
                      onClick={() => setFilterOptions(undefined)}>
                      <CosmosButton
                        kind="bare"
                        size="small"
                        width={buttonWidth}
                        hovered={!filterOptions}>
                        {getTrans(lang.All).toUpperCase()}
                      </CosmosButton>
                    </li>
                    {Object.entries(filterVals).map(([key, values]) =>
                      values.map(({ label, value, operator }) => (
                        <li
                          key={`${key}-${getValue(value)}`}
                          className={`filterValue ${filterOptions &&
                              filterOptions.operator === operator &&
                              filterOptions.value === getValue(value) &&
                              filterOptions.field === key
                              ? ' active'
                              : 'null'
                            }`}
                          onClick={() => {
                            if (
                              filterOptions &&
                              filterOptions.operator === operator &&
                              filterOptions.value === getValue(value) &&
                              filterOptions.field === key
                            ) {
                              setFilterOptions(undefined);
                            } else {
                              setFilterOptions({
                                operator: operator,
                                value: getValue(value),
                                field: key
                              });
                            }
                          }}
                        >
                          <CosmosButton
                            kind="bare"
                            size="small"
                            width={buttonWidth}
                            hovered={
                              filterOptions &&
                              filterOptions.operator === operator &&
                              filterOptions.value === getValue(value) &&
                              filterOptions.field === key
                            }>
                            {getValue(label).toUpperCase()}
                          </CosmosButton>
                        </li>
                      ))
                    )}
                  </ul>
                </div>
              )}
            </div>
          </ModalHeader>
          <ModalBody>
            <PerfectScrollbar
              style={{ maxHeight: `calc(100vh - ${headerBounds.height}px)` }}
              options={{ wheelPropagation: true }}>
              <motion.div
                variants={containerAnimation}
                initial="hidden"
                animate="visible"
                className="options-inner options-striped-columns">
                {Array.isArray(options) &&
                  options
                    .filter(
                      ({ searchLabel }) =>
                        !filter || searchLabel.toLowerCase().includes(filter.toLowerCase())
                    )
                    .filter((entry) => {
                      if (!filterOptions) return true;
                      switch (filterOptions.operator) {
                        case 'lt':
                          return (
                            entry &&
                            entry[filterOptions.field] &&
                            parseInt(entry[filterOptions.field]) <= filterOptions.value
                          );
                        case 'gt':
                          return (
                            entry &&
                            entry[filterOptions.field] &&
                            parseInt(entry[filterOptions.field]) >= filterOptions.value
                          );
                        case 'eq':
                          return (
                            entry &&
                            entry[filterOptions.field] &&
                            //eslint-disable-next-line
                            entry[filterOptions.field] === filterOptions.value
                          );
                        default:
                          return true;
                      }
                    })
                    .map((data, index) => {
                      return (
                        <motion.div
                          key={`select-${name}-${data.value}`}
                          variants={index < 20 && itemAnimation}
                          positionTransition
                          className={`option${data.value === currentValue.value ? ' active' : ''
                            }${disabledOptions &&
                              disabledOptions.includes(data.value) &&
                              data.value !== currentValue.value
                              ? ' disabled'
                              : ''
                            }`}
                          onClick={() => {
                            if (
                              (disabledOptions && !disabledOptions.includes(data.value)) ||
                              (!disabledOptions && data.value !== currentValue.value)
                            ) {
                              if (typeof onChange === 'function') onChange(data);
                              setVisible(false);
                            }
                          }}>
                          {data.label.props !== undefined &&
                            typeof data.label.props.children === 'string'
                            ? getValue(data.label.props.children)
                            : data.label}
                          {(!data) &&
                            <CosmosSpinner style={{ width: '48px', height: '48px' }} />
                          }
                        </motion.div>
                      );
                    })}
              </motion.div>
            </PerfectScrollbar>
          </ModalBody>
        </Modal>
      </div>
    </>
  );
};
export { ModalSelect };
